import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Smartphone } from "react-feather";
import { API_BASE, apiFetch } from "../api";
import { useTheme } from "../theme/ThemeContext";

// User analytics for admins: how many people use the app, whether they are
// actually doing anything, and which features they use. Counts and dates only;
// nobody's amounts or entries are shown.

const pct = (n, total) => (total ? Math.round((n / total) * 100) : 0);

const formatDay = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

const ago = (isoTime) => {
  if (!isoTime) return "Never";
  const mins = Math.round((Date.now() - new Date(isoTime).getTime()) / 60000);
  if (mins < 2) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "Yesterday" : `${days} days ago`;
};

const STATUS_HELP = {
  Active: "Used the app in the last 7 days",
  Idle: "Last used 8 to 30 days ago",
  Inactive: "Not used for over 30 days",
  "Not started": "Signed up but hasn't saved anything",
};

const Panel = ({ title, note, children }) => {
  const { tokens, fonts } = useTheme();
  return (
    <section style={{ backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, borderRadius: 16, boxShadow: tokens.cardShadow, padding: 16, marginTop: 14 }}>
      <h2 style={{ margin: 0, fontFamily: fonts.display, fontSize: 16, color: tokens.textPrimary }}>{title}</h2>
      {note && <p style={{ margin: "4px 0 0", fontSize: 12, color: tokens.textMuted }}>{note}</p>}
      <div style={{ marginTop: 12 }}>{children}</div>
    </section>
  );
};

const Tile = ({ label, value, sub }) => {
  const { tokens, fonts } = useTheme();
  return (
    <div style={{ backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, borderRadius: 14, padding: "12px 14px", boxShadow: tokens.cardShadow }}>
      <div style={{ fontSize: 12, color: tokens.textMuted }}>{label}</div>
      <div style={{ marginTop: 4, fontFamily: fonts.display, fontSize: 26, fontWeight: 700, color: tokens.textPrimary }}>{value}</div>
      {sub && <div style={{ marginTop: 2, fontSize: 11, color: tokens.textMuted }}>{sub}</div>}
    </div>
  );
};

// Daily active users for the last 30 days. One series, so no legend: the title
// names it. Hover or tap a day for its numbers.
const DailyChart = ({ daily }) => {
  const { tokens, fonts } = useTheme();
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...daily.map((d) => d.active));
  const HEIGHT = 140;
  const current = hover !== null ? daily[hover] : null;

  return (
    <div>
      <div style={{ position: "relative", display: "flex", gap: 8 }}>
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: HEIGHT, fontSize: 11, color: tokens.textMuted, fontFamily: fonts.mono, textAlign: "right", minWidth: 18 }}>
          <span>{max}</span>
          <span>0</span>
        </div>
        <div
          style={{ position: "relative", flex: 1, height: HEIGHT, display: "flex", alignItems: "flex-end", gap: 2, borderBottom: `1px solid ${tokens.surfaceBorder}`, borderTop: `1px dashed ${tokens.surfaceBorder}` }}
          onMouseLeave={() => setHover(null)}
        >
          {daily.map((d, i) => (
            <button
              key={d.day}
              type="button"
              aria-label={`${formatDay(d.day)}: ${d.active} active, ${d.writes} changes, ${d.signups} sign-ups`}
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              onClick={() => setHover(hover === i ? null : i)}
              style={{ flex: 1, height: "100%", padding: 0, border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "flex-end" }}
            >
              <span
                style={{
                  display: "block",
                  width: "100%",
                  height: d.active ? `${Math.max(3, (d.active / max) * 100)}%` : 0,
                  borderRadius: "4px 4px 0 0",
                  backgroundColor: tokens.chartLine,
                  opacity: hover === null || hover === i ? 1 : 0.45,
                }}
              />
            </button>
          ))}
          {current && (
            <div
              role="status"
              style={{
                position: "absolute",
                top: 0,
                left: `${Math.min(70, Math.max(0, (hover / daily.length) * 100 - 10))}%`,
                padding: "8px 10px",
                borderRadius: 10,
                fontSize: 12,
                lineHeight: 1.5,
                backgroundColor: tokens.topbarBg,
                color: tokens.topbarText,
                boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
                pointerEvents: "none",
                whiteSpace: "nowrap",
              }}
            >
              <div style={{ fontWeight: 700 }}>{formatDay(current.day)}</div>
              <div>{current.active} active {current.active === 1 ? "user" : "users"}</div>
              <div>{current.writes} changes made</div>
              <div>{current.signups} sign-{current.signups === 1 ? "up" : "ups"}</div>
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginLeft: 26, marginTop: 6, fontSize: 11, color: tokens.textMuted }}>
        <span>{formatDay(daily[0].day)}</span>
        <span>{formatDay(daily[Math.floor(daily.length / 2)].day)}</span>
        <span>Today</span>
      </div>
    </div>
  );
};

// A labelled bar per row, for "how many users" breakdowns.
const BarList = ({ rows, total }) => {
  const { tokens, fonts } = useTheme();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {rows.map((r) => (
        <div key={r.label}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, color: tokens.textPrimary }}>
            <span>
              {r.label}
              {r.help && <span style={{ marginLeft: 6, fontSize: 11, color: tokens.textMuted }}>{r.help}</span>}
            </span>
            <span style={{ fontFamily: fonts.mono, fontWeight: 700, whiteSpace: "nowrap" }}>
              {r.value} <span style={{ fontWeight: 400, color: tokens.textMuted }}>· {pct(r.value, total)}%</span>
            </span>
          </div>
          <div style={{ height: 6, marginTop: 5, borderRadius: 3, backgroundColor: tokens.neutralSoft, overflow: "hidden" }}>
            <div style={{ width: `${pct(r.value, total)}%`, height: "100%", borderRadius: 3, backgroundColor: tokens.chartLine }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const { tokens } = useTheme();
  const colors = {
    Active: [tokens.positiveBg, tokens.positive],
    Idle: [tokens.pendingBg, tokens.pending],
    Inactive: [tokens.neutralSoft, tokens.textMuted],
    "Not started": [tokens.neutralSoft, tokens.negative],
  }[status];
  return <span style={{ padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, backgroundColor: colors[0], color: colors[1], whiteSpace: "nowrap" }}>{status}</span>;
};

const PERSON_FILTERS = ["All", "Active", "Idle", "Inactive", "Not started"];

const People = ({ people }) => {
  const { tokens, fonts } = useTheme();
  const [filter, setFilter] = useState("All");
  const shown = people.filter((p) => filter === "All" || p.status === filter);

  return (
    <>
      <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 6 }}>
        {PERSON_FILTERS.map((f) => {
          const n = f === "All" ? people.length : people.filter((p) => p.status === f).length;
          const active = filter === f;
          return (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              style={{ flexShrink: 0, padding: "7px 12px", borderRadius: 999, border: `1px solid ${active ? "transparent" : tokens.surfaceBorder}`, backgroundColor: active ? tokens.accent : tokens.surface, color: active ? tokens.accentContrast : tokens.textMuted, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              {f} · {n}
            </button>
          );
        })}
      </div>
      {shown.length === 0 && <p style={{ textAlign: "center", fontSize: 13, color: tokens.textMuted, padding: "20px 0" }}>Nobody here.</p>}
      {shown.map((p) => (
        <div key={p.id} style={{ padding: "12px 0", borderTop: `1px solid ${tokens.surfaceBorder}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: tokens.textPrimary, display: "flex", alignItems: "center", gap: 6 }}>
                {p.username}
                {p.devices.length > 0 && <Smartphone size={13} aria-label={`Phone app: ${p.devices.join(", ")}`} color={tokens.textMuted} />}
              </div>
              <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.email || "No email"}</div>
            </div>
            <StatusPill status={p.status} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: "6px 12px", marginTop: 8, fontSize: 12, color: tokens.textMuted }}>
            <span>Joined <b style={{ color: tokens.textPrimary }}>{p.joined ? formatDay(p.joined) : "?"}</b></span>
            <span>Last seen <b style={{ color: tokens.textPrimary }}>{ago(p.lastSeenAt)}</b></span>
            <span>Days used (30d) <b style={{ color: tokens.textPrimary, fontFamily: fonts.mono }}>{p.activeDays30}</b></span>
            <span>Changes (30d) <b style={{ color: tokens.textPrimary, fontFamily: fonts.mono }}>{p.writes30}</b></span>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: tokens.textMuted }}>
            Saved: {p.counts.bills} bills · {p.counts.income} income · {p.counts.tasks} tasks · {p.counts.assets} accounts · {p.counts.loans} loans
            {p.counts.sms > 0 ? ` · ${p.counts.sms} from SMS` : ""}
          </div>
        </div>
      ))}
    </>
  );
};

const AdminDashboard = () => {
  const { tokens } = useTheme();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    apiFetch(`${API_BASE}/admin/stats`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "Couldn't load the numbers.");
        setStats(data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <p role="alert" style={{ color: tokens.negative, fontSize: 14 }}>{error}</p>;
  if (!stats) return <p style={{ color: tokens.textMuted, fontSize: 14, textAlign: "center", marginTop: 40 }}>Loading…</p>;

  const t = stats.totals;
  return (
    <div style={{ color: tokens.textPrimary }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <p style={{ margin: 0, fontSize: 12, color: tokens.textMuted }}>
          Updated {ago(stats.generatedAt).toLowerCase()}
          {stats.trackingSince ? ` · activity counted since ${formatDay(stats.trackingSince)}` : ""}
        </p>
        <button type="button" onClick={load} disabled={loading} aria-label="Refresh" style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 999, border: `1px solid ${tokens.surfaceBorder}`, background: tokens.surface, color: tokens.textPrimary, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
          <RefreshCw size={13} /> {loading ? "Loading" : "Refresh"}
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginTop: 12 }}>
        <Tile label="Total users" value={t.users} sub={`+${t.newUsers7} this week · +${t.newUsers30} in 30 days`} />
        <Tile label="Active today" value={t.activeToday} sub={`${pct(t.activeToday, t.users)}% of users`} />
        <Tile label="Active in 7 days" value={t.active7} sub={`${pct(t.active7, t.users)}% of users`} />
        <Tile label="Active in 30 days" value={t.active30} sub={`${pct(t.active30, t.users)}% of users`} />
        <Tile label="Added or changed something" value={t.addedSomething7} sub="in the last 7 days" />
        <Tile label="Signed up, saved nothing" value={t.notStarted} sub={`${pct(t.notStarted, t.users)}% of users`} />
        <Tile label="Using the phone app" value={t.withPhoneApp} sub="registered for push alerts" />
      </div>

      <Panel title="Active users per day" note="People who opened the app each day, last 30 days. Hover or tap a day for details.">
        <DailyChart daily={stats.daily} />
      </Panel>

      <Panel title="Is anyone doing anything?" note="Every user falls in exactly one group.">
        <BarList total={t.users} rows={stats.status.map((s) => ({ label: s.status, help: STATUS_HELP[s.status], value: s.users }))} />
      </Panel>

      <Panel title="Features in use" note="Users who have saved at least one of each.">
        <BarList total={t.users} rows={stats.features.map((f) => ({ label: f.feature, value: f.users }))} />
      </Panel>

      <Panel title="Users" note="Most recently seen first. Only counts are shown, never anyone's amounts or entries.">
        <People people={stats.people} />
      </Panel>
    </div>
  );
};

export default AdminDashboard;
